import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { CrudPage, type FormField } from '@/components/CrudPage';
import PurgeBot from '@/components/PurgeBot';
import { Badge, Card } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

interface Bot {
  id: string;
  name: string;
  code: string;
  status: string;
  bot_group_id: string | null;
  max_concurrent: number;
  config: Record<string, unknown>;
  created_at: string;
}

interface BotGroup {
  id: string;
  name: string;
}

interface BotTemplate {
  id: string;
  key: string;
  name: string;
  description: string | null;
}

const STATUS_TONES: Record<string, 'green' | 'amber' | 'gray'> = {
  ACTIVE: 'green',
  PAUSED: 'amber',
  DRAFT: 'gray',
  ARCHIVED: 'gray',
};

export default function Bots() {
  const navigate = useNavigate();

  const { data: groups } = useQuery({
    queryKey: ['bot-groups-select'],
    queryFn: () => api.get<{ data: BotGroup[] }>('/bot-groups', { page_size: 200 }),
  });

  // The vertical the bot is for. Six of these ship — Home Services, ACA
  // Health, Auto Insurance, Medicare, Solar and Final Expense — and the form
  // offered none of them, so every bot ever created here silently became a
  // Home Services bot. That is not cosmetic: the template's `default_config`
  // is merged underneath the bot's own on every call, so it decides the
  // opening line, the qualifying questions and the services on offer.
  const { data: templates } = useQuery({
    queryKey: ['bot-templates-select'],
    queryFn: () => api.get<{ data: BotTemplate[] }>('/bot-templates'),
  });

  const engines = (templates?.data ?? []).filter((t) => t.key !== 'soundboard');

  const fields: FormField[] = [
    { name: 'name', label: 'Name', required: true, placeholder: 'Glee AI Bot 01' },
    {
      name: 'code',
      label: 'Code',
      required: true,
      immutable: true,
      placeholder: 'GLEE01',
      hint: 'Stable external reference. Letters, numbers, dash and underscore only.',
    },
    {
      name: 'template_id',
      label: 'Bot engine',
      type: 'select',
      // Create only. Changing a live bot's engine underneath it would swap
      // its script, its questions and its services while it is assigned to a
      // running campaign.
      only: 'create',
      required: true,
      options: engines.map((e) => ({ value: e.id, label: e.name })),
      hint: 'Which vertical this bot sells. It sets the opening line, the qualifying questions and the services on offer.',
    },
    {
      name: 'bot_group_id',
      label: 'Bot group',
      type: 'select',
      options: (groups?.data ?? []).map((g) => ({ value: g.id, label: g.name })),
      hint: 'Optional — groups organise bots within a client.',
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      defaultValue: 'ACTIVE',
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'PAUSED', label: 'Paused' },
        { value: 'DRAFT', label: 'Draft' },
      ],
    },
    {
      name: 'max_concurrent',
      label: 'Max concurrent calls',
      type: 'number',
      defaultValue: 10,
    },
  ];

  return (
    <CrudPage<Bot>
      title="Glee AI Bots"
      description="Each bot runs one engine — the vertical it sells — with your own script and services on top."
      resource="bots"
      path="/bots"
      queryKey="bots"
      searchPlaceholder="Search by name or code…"
      deletable
      describeRow={(bot) => bot.name}
      deleteExtra={(bot) => <PurgeBot id={bot.id} name={bot.name} />}
      deleteExplanation={
        <>
          <p>
            The bot is <strong>archived</strong>, not erased. Its calls, its
            transcripts and its recordings stay exactly where they are — those
            are the record of what was said to real people and are not ours to
            throw away.
          </p>
          <p className="text-xs text-ink-500">
            It stops being dialled and disappears from this list. Any
            assignment pointing at it stops matching, so check Assignments if
            a campaign goes quiet. A call already in progress is not cut off.
          </p>
          <p className="text-xs text-ink-500">
            To see it again, filter this page by <strong>Archived</strong>.
          </p>
        </>
      }
      emptyTitle="No bots yet"
      emptyDescription="Create a bot, pick the vertical it sells, and assign it to a campaign to start taking calls."
      createLabel="New bot"
      onRowClick={(bot) => navigate(`/bots/${bot.id}`)}
      filters={[
        {
          key: 'status',
          label: 'Status',
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'PAUSED', label: 'Paused' },
            { value: 'DRAFT', label: 'Draft' },
            { value: 'ARCHIVED', label: 'Archived' },
          ],
        },
      ]}
      fields={fields}
      columns={[
        {
          key: 'name',
          header: 'Bot',
          render: (bot) => (
            <div>
              <div className="font-medium text-ink-900 dark:text-ink-50">{bot.name}</div>
              <div className="font-mono text-xs text-ink-500">{bot.code}</div>
            </div>
          ),
        },
        {
          key: 'company',
          header: 'Company identity',
          render: (bot) => {
            const company = (bot.config?.company ?? {}) as { name?: string };
            return company.name ? (
              <span className="text-ink-700 dark:text-ink-200">{company.name}</span>
            ) : (
              <span className="text-ink-400">Template default</span>
            );
          },
        },
        {
          key: 'transfer',
          header: 'Transfer to',
          render: (bot) => {
            const transfer = (bot.config?.transfer ?? {}) as { in_group?: string };
            return transfer.in_group ? (
              <Badge tone="blue">{transfer.in_group}</Badge>
            ) : (
              <span className="text-ink-400">Not set</span>
            );
          },
        },
        {
          key: 'status',
          header: 'Status',
          render: (bot) => (
            <Badge tone={STATUS_TONES[bot.status] ?? 'gray'}>{bot.status}</Badge>
          ),
        },
        {
          key: 'max_concurrent',
          header: 'Max calls',
          numeric: true,
          render: (bot) => bot.max_concurrent,
        },
        {
          key: 'created_at',
          header: 'Created',
          render: (bot) => (
            <span className="text-xs text-ink-500">{formatDateTime(bot.created_at)}</span>
          ),
        },
      ]}
    >
      <Card className="mb-4">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          <strong className="text-ink-900 dark:text-ink-50">One bot, every vertical.</strong>{' '}
          A single Glee AI Bot handles HVAC, roofing, plumbing, windows, solar and the rest
          in the same conversation. If a customer raises two services, the bot captures both
          and keeps going — there is no per-vertical bot to route between.
        </p>
      </Card>
    </CrudPage>
  );
}
